import { useAgent } from "@watermarkinsights/ai-toolbox";
import { localDateWithTimeStamp } from "@/features/common/helper";
import aiChatIconsSprite from "./AiChatIcons.svg";
import type { MessagePair } from "./draft-message/DraftConversationPanel";
import {
  extractSourceIdFromToolArgs,
  resolveSourceProducts,
} from "./ai-chat-source-products";
import type { SourceProductConfig, SourceProductId } from "./ai-chat-source-products";

// ── Types ──────────────────────────────────────────────────────────────────────

export type AgentMessage = ReturnType<typeof useAgent>["messages"][number];

export type ExportFormat = "pdf" | "txt";

export type BarChartDataItem = {
  barLegend: string;
  amount: number;
  groupName?: string;
  popoverTitle?: string;
  popoverText?: string;
};

export type BarChartPayload = {
  chartType: "bar";
  label?: string;
  categoryAxisLabel: string;
  valueAxisLabel?: string;
  valueFormat?: "amount" | "percentage" | "none";
  sort?: "ascending" | "descending" | "none";
  data: BarChartDataItem[];
};

export type PieChartDataItem = {
  legend: string;
  amount: number;
};

export type PieChartPayload = {
  chartType: "pie";
  label?: string;
  categoryAxisLabel: string;
  valueLabel: string;
  valueFormat?: "amount" | "percentage" | "none";
  data: PieChartDataItem[];
};

export type LineChartDataPoint = {
  x: string;
  y: number | null;
};

export type LineChartSeries = {
  label: string;
  data: LineChartDataPoint[];
};

export type LineChartPayload = {
  chartType: "line";
  label?: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  units?: string;
  showGrid?: boolean;
  visibilityToggles?: boolean;
  showDeltas?: boolean;
  data: LineChartSeries[];
};

export type SuggestedQuestionsPayload = {
  toolType: "suggestedQuestions";
  label?: string;
  questions: string[];
};

// ── Utilities ──────────────────────────────────────────────────────────────────

type AssistantToolCalls = Extract<
  AgentMessage,
  { role: "assistant" }
>["toolCalls"];

export const buildSvgIconHref = (iconId: string): string =>
  `${aiChatIconsSprite}#${iconId}`;

export const extractTextFromMessageContent = (
  content: AgentMessage["content"],
): string => {
  if (typeof content === "string") return content;
  if (Array.isArray(content)) {
    return content
      .map((block) => (block.type === "text" ? block.text : `[${block.type}]`))
      .join(" ");
  }
  return "";
};

export const extractToolNameFromAssistantMessage = (
  message: AgentMessage,
): string | null => {
  if (message.role !== "assistant") return null;
  const assistantMessage = message as Extract<
    AgentMessage,
    { role: "assistant" }
  >;
  if (extractTextFromMessageContent(assistantMessage.content).trim())
    return null;
  return assistantMessage.toolCalls?.[0]?.function?.name ?? null;
};

export const extractFirstMatchingToolCallPayload = <T>(
  toolCalls: AssistantToolCalls,
  parser: (args: string) => T | null,
): T | null =>
  toolCalls?.reduce<T | null>((found, toolCall) => {
    if (found) return found;
    return toolCall.function?.arguments
      ? parser(toolCall.function.arguments)
      : null;
  }, null) ?? null;

export const parseBarChartFromToolArgs = (
  args: string,
): BarChartPayload | null => {
  try {
    const parsed = JSON.parse(args);
    if (
      parsed.chartType === "bar" &&
      Array.isArray(parsed.data) &&
      parsed.data.length > 0
    ) {
      return parsed as BarChartPayload;
    }
  } catch {}
  return null;
};

export const parsePieChartFromToolArgs = (
  args: string,
): PieChartPayload | null => {
  try {
    const parsed = JSON.parse(args);
    if (
      parsed.chartType === "pie" &&
      Array.isArray(parsed.data) &&
      parsed.data.length > 0
    ) {
      return parsed as PieChartPayload;
    }
  } catch {}
  return null;
};

export const parseLineChartFromToolArgs = (
  args: string,
): LineChartPayload | null => {
  try {
    const parsed = JSON.parse(args);
    if (
      parsed.chartType === "line" &&
      Array.isArray(parsed.data) &&
      parsed.data.length > 0
    ) {
      return parsed as LineChartPayload;
    }
  } catch {}
  return null;
};

export const parseSuggestedQuestionsFromToolArgs = (
  args: string,
): SuggestedQuestionsPayload | null => {
  try {
    const parsed = JSON.parse(args);
    if (
      parsed.toolType === "suggestedQuestions" &&
      Array.isArray(parsed.questions) &&
      parsed.questions.length > 0
    ) {
      return parsed as SuggestedQuestionsPayload;
    }
  } catch {}
  return null;
};

export type TablePayload = {
  toolType: "renderTable";
  columns: string[];
  rows: (string | number)[][];
  title?: string;
};

export const parseTableFromToolArgs = (args: string): TablePayload | null => {
  try {
    const parsed = JSON.parse(args);
    if (
      parsed.toolType === "renderTable" &&
      Array.isArray(parsed.columns) &&
      parsed.columns.length > 0 &&
      Array.isArray(parsed.rows) &&
      parsed.rows.length > 0
    ) {
      return parsed as TablePayload;
    }
  } catch {}
  return null;
};

// ── Message list derivation ─────────────────────────────────────────────────────

export const findSuggestedQuestionsForCurrentTurn = (
  visibleMessages: AgentMessage[],
): SuggestedQuestionsPayload | null => {
  const lastUserMessageIndex = visibleMessages
    .map((message) => message.role)
    .lastIndexOf("user");

  return visibleMessages
    .slice(lastUserMessageIndex + 1)
    .reduce<SuggestedQuestionsPayload | null>(
      (found, message) =>
        found || message.role !== "assistant"
          ? found
          : extractFirstMatchingToolCallPayload(
              message.toolCalls,
              parseSuggestedQuestionsFromToolArgs,
            ),
      null,
    );
};

export const deriveVisibleMessages = (
  messages: AgentMessage[],
  clearedMessageCount: number,
): AgentMessage[] =>
  messages
    .slice(clearedMessageCount)
    .filter((message) => message.role === "user" || message.role === "assistant")
    .filter((message, index, allMessages) => {
      const toolName = extractToolNameFromAssistantMessage(message);
      return (
        !toolName ||
        extractToolNameFromAssistantMessage(allMessages[index - 1]) !== toolName
      );
    });

export const hasExportableAssistantContent = (message: AgentMessage): boolean => {
  if (message.role !== "assistant") return false;
  const assistantMessage = message as Extract<AgentMessage, { role: "assistant" }>;
  if (extractTextFromMessageContent(assistantMessage.content).trim()) return true;
  const toolCalls = assistantMessage.toolCalls;
  return Boolean(
    extractFirstMatchingToolCallPayload(toolCalls, parseBarChartFromToolArgs) ||
      extractFirstMatchingToolCallPayload(toolCalls, parsePieChartFromToolArgs) ||
      extractFirstMatchingToolCallPayload(toolCalls, parseLineChartFromToolArgs) ||
      extractFirstMatchingToolCallPayload(toolCalls, parseTableFromToolArgs),
  );
};

export const buildExportableMessages = (
  visibleMessages: AgentMessage[],
): AgentMessage[] =>
  visibleMessages.filter(
    (message) => message.role === "user" || hasExportableAssistantContent(message),
  );

export const buildEndMessageSourceProductsByMessageId = (
  messages: AgentMessage[],
  isRunning: boolean,
): Record<string, SourceProductConfig[]> => {
  const sourceProductsByMessageId: Record<string, SourceProductConfig[]> = {};
  let turnProductIds = new Set<SourceProductId>();
  let turnEndMessageId: string | null = null;

  const flushTurn = (skip: boolean) => {
    if (turnEndMessageId && !skip) {
      if (turnProductIds.size > 0) {
        sourceProductsByMessageId[turnEndMessageId] = resolveSourceProducts(
          Array.from(turnProductIds),
        );
      }
    }
    turnProductIds = new Set();
    turnEndMessageId = null;
  };

  messages.forEach((message) => {
    if (message.role === "user") {
      flushTurn(false);
      return;
    }
    if (message.role !== "assistant") return;

    message.toolCalls?.forEach((toolCall) => {
      const args = toolCall.function?.arguments;
      if (!args) return;
      const sourceProductId = extractSourceIdFromToolArgs(args);
      if (sourceProductId) {
        turnProductIds.add(sourceProductId);
      }
    });

    if (hasExportableAssistantContent(message)) {
      turnEndMessageId = message.id;
    }
  });
  flushTurn(isRunning);

  return sourceProductsByMessageId;
};

// ── Text export ──────────────────────────────────────────────────────────────

const triggerBlobDownload = (
  content: string,
  mimeType: string,
  fileName: string,
): void => {
  const blob = new Blob([content], { type: mimeType });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

export type ChatTranscriptLabels = {
  user: string;
  assistant: string;
};

export type TableSortState = {
  column: string | null;
  dir: "asc" | "desc";
};

export type TableSortByMessageId = Record<string, TableSortState>;

export type FormatChatTranscriptOptions = {
  includeTableData?: boolean;
  tableSortByMessageId?: TableSortByMessageId;
};

export const sortTableRows = (
  columns: string[],
  rows: (string | number)[][],
  sortColumn: string | null,
  sortDirection: "asc" | "desc",
): (string | number)[][] => {
  const sortColumnIndex = sortColumn !== null ? columns.indexOf(sortColumn) : -1;
  if (sortColumnIndex < 0) return rows;
  return [...rows].sort((firstRow, secondRow) => {
    const firstCellValue = firstRow[sortColumnIndex];
    const secondCellValue = secondRow[sortColumnIndex];
    const comparisonResult =
      typeof firstCellValue === "number" && typeof secondCellValue === "number"
        ? (firstCellValue < secondCellValue ? -1 :
           firstCellValue > secondCellValue ? 1 : 0)
        : String(firstCellValue).localeCompare(String(secondCellValue), undefined, {
            numeric: true,
          });
    return sortDirection === "asc" ? comparisonResult : -comparisonResult;
  });
};

export const formatTableAsMarkdown = (
  payload: TablePayload | null,
  sort?: TableSortState,
): string | null => {
  if (!payload) return null;
  const header = `| ${payload.columns.join(" | ")} |`;
  const separator = `| ${payload.columns.map(() => "---").join(" | ")} |`;
  const sortedRows = sortTableRows(
    payload.columns,
    payload.rows,
    sort ? sort.column : (payload.columns[0] ?? null),
    sort ? sort.dir : "asc",
  );
  const rows = sortedRows.map((row) => `| ${row.join(" | ")} |`);
  const table = [header, separator, ...rows].join("\n");
  return payload.title ? `${payload.title}\n${table}` : table;
};

export const formatChatTranscript = (
  messages: AgentMessage[],
  labels: ChatTranscriptLabels,
  options: FormatChatTranscriptOptions = {},
): string =>
  messages
    .map((message) => {
      const text = extractTextFromMessageContent(message.content).trim();
      const tableText =
        options.includeTableData && message.role === "assistant"
          ? formatTableAsMarkdown(
              extractFirstMatchingToolCallPayload(
                message.toolCalls,
                parseTableFromToolArgs,
              ),
              options.tableSortByMessageId?.[message.id],
            )
          : null;
      const combinedText = [text, tableText].filter(Boolean).join("\n\n");
      if (!combinedText) return null;
      const speaker = message.role === "user" ? labels.user : labels.assistant;
      return `${speaker}: ${combinedText}`;
    })
    .filter((line): line is string => line !== null)
    .join("\n\n");

export const downloadChatTranscript = (
  messages: AgentMessage[],
  labels: ChatTranscriptLabels,
  fileNamePrefix: string = "Currents_Conversation",
  options?: FormatChatTranscriptOptions,
): void => {
  const transcript = formatChatTranscript(messages, labels, options);
  if (!transcript) return;

  triggerBlobDownload(
    transcript,
    "text/plain;charset=utf-8",
    `${fileNamePrefix}_${localDateWithTimeStamp()}.txt`,
  );
};

// ── CSV export ──────────────────────────────────────────────────────────────

const escapeCsvField = (value: string | number): string => {
  const stringValue = String(value);
  return /[",\r\n]/.test(stringValue)
    ? `"${stringValue.replace(/"/g, '""')}"`
    : stringValue;
};

export const formatTableAsCsv = (
  columns: string[],
  rows: (string | number)[][],
): string =>
  [columns, ...rows]
    .map((line) => line.map(escapeCsvField).join(","))
    .join("\r\n");

export const downloadTableAsCsv = (
  columns: string[],
  rows: (string | number)[][],
  title?: string,
): void => {
  const csv = formatTableAsCsv(columns, rows);
  const utf8Bom = "﻿"; // ensures Excel detects UTF-8 encoding instead of misreading special characters

  triggerBlobDownload(
    utf8Bom + csv,
    "text/csv;charset=utf-8",
    `${title || "Currents_Conversation_Table"}_${localDateWithTimeStamp()}.csv`,
  );
};

export type ChartCsvData = {
  columns: string[];
  rows: (string | number)[][];
};

export const buildBarChartCsvData = (
  data: BarChartDataItem[],
  categoryAxisLabel: string,
  valueAxisLabel?: string,
): ChartCsvData => {
  const isGrouped = data.some((item) => Boolean(item.groupName));
  const categoryColumn = categoryAxisLabel || "Category";
  const valueColumn = valueAxisLabel || "Amount";

  if (isGrouped) {
    return {
      columns: [categoryColumn, "Series", valueColumn],
      rows: data.map((item) => [item.groupName ?? "", item.barLegend, item.amount]),
    };
  }

  return {
    columns: [categoryColumn, valueColumn],
    rows: data.map((item) => [item.barLegend, item.amount]),
  };
};

export const buildPieChartCsvData = (
  data: PieChartDataItem[],
  categoryAxisLabel: string,
  valueLabel: string,
): ChartCsvData => ({
  columns: [categoryAxisLabel || "Category", valueLabel || "Amount"],
  rows: data.map((item) => [item.legend, item.amount]),
});

export const buildLineChartCsvData = (payload: LineChartPayload): ChartCsvData => {
  const { data, xAxisLabel } = payload;

  const xValues: string[] = [];
  const seenXValues = new Set<string>();
  data.forEach((series) =>
    series.data.forEach((point) => {
      if (!seenXValues.has(point.x)) {
        seenXValues.add(point.x);
        xValues.push(point.x);
      }
    }),
  );

  const columns = [xAxisLabel || "X", ...data.map((series) => series.label)];
  const rows = xValues.map((x) => [
    x,
    ...data.map((series) => series.data.find((point) => point.x === x)?.y ?? ""),
  ]);

  return { columns, rows };
};

export const buildMessagePairs = (
  visibleMessages: AgentMessage[],
): MessagePair[] =>
  visibleMessages.reduce<MessagePair[]>((messagePairs, message, index) => {
    if (message.role !== "assistant") return messagePairs;
    const response = extractTextFromMessageContent(message.content).trim();
    if (!response) return messagePairs;
    const previousMessage = visibleMessages[index - 1];
    const question =
      previousMessage?.role === "user"
        ? extractTextFromMessageContent(previousMessage.content)
        : "";
    messagePairs.push({ id: message.id, question, response });
    return messagePairs;
  }, []);
